import React, { useRef } from "react";
import { Helmet } from "react-helmet";
import { Typography, Container, Box, Divider } from "@mui/material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer/Footer";
import InvestorBanner from "../components/InvestorBanner/InvestorBanner";
import useScrollToTop from "../ScrollToTop";

const Refund = () => {
  const footerRef = useRef(null);

  useScrollToTop();

  const BodyText = ({ children }) => (
    <Typography
      sx={{
        fontSize: { xs: "13px", md: "14px" },
        lineHeight: 1.8,
        color: "#2c2c2c",
        mb: 2,
        textAlign: "justify",
      }}
    >
      {children}
    </Typography>
  );

  const BulletPoint = ({ children }) => (
    <Box sx={{ display: "flex", gap: 1.5, mb: 2, pl: 2 }}>
      <Box
        sx={{
          minWidth: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#28947F",
          mt: 1,
        }}
      />
      <Typography
        sx={{
          fontSize: { xs: "13px", md: "14px" },
          lineHeight: 1.8,
          color: "#2c2c2c",
          textAlign: "justify",
        }}
      >
        {children}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(90deg, rgba(209,230,226,1) 0%, rgba(234,238,236,1) 95%)`,
      }}
    >
      <Helmet>
        <title>Refund & Settlement Policy - InvestoFarms</title>
        <meta
          name="description"
          content="Understand our refund and settlement policy for managed farmland leasing. Review conditions and adjustments for a transparent process."
        />
      </Helmet>

      <Navbar />

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        <Box sx={{ maxWidth: "900px", margin: "0 auto" }}>
          <Typography
            sx={{
              fontSize: { xs: "26px", md: "34px" },
              fontWeight: "700",
              color: "#28947F",
              textAlign: "center",
              mb: 4,
            }}
          >
            Refund & Settlement Policy
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "17px", md: "19px" },
              fontWeight: "600",
              color: "#28947F",
              mb: 2,
            }}
          >
            Conditions for Refund
          </Typography>

          <BodyText>
            If the Sub-Lease Agreement is terminated after the completion of the
            mandatory Lock-in Period, the Company shall process the refund of
            the Security Deposit (or Refundable Lease Advance), subject to the
            following reconciliation adjustments:
          </BodyText>

          <Box sx={{ mb: 3 }}>
            <BulletPoint>
              <strong style={{ color: "#28947F" }}>
                Adjustment of Harvest Returns:
              </strong>{" "}
              The total value of any harvest sale proceeds previously remitted
              to the Client, or the value of any fresh produce delivered to the
              Client (calculated at prevailing market rates), shall be deducted
              from the final refundable amount.
            </BulletPoint>

            <BulletPoint>
              <strong style={{ color: "#28947F" }}>
                Non-Refundable Service Fees:
              </strong>{" "}
              For the purpose of this settlement calculation, any Managed
              Farming Fees or sales facilitation commissions originally deducted
              by the Company at the time of harvest sale are non-refundable.
              While these amounts may be notionally considered to calculate the
              Client's total realized value ("Adjusted Entitlement"), they will
              not be refunded by the Company.
            </BulletPoint>
          </Box>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          <Box
            sx={{
              mt: 4,
              p: 3,
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              borderRadius: 2,
              border: "1px solid rgba(40, 148, 127, 0.3)",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "15px", md: "16px" },
                fontWeight: "600",
                color: "#28947F",
                mb: 1,
              }}
            >
              Get in Touch with Us
            </Typography>
            <Typography
              sx={{ fontSize: { xs: "13px", md: "14px" }, color: "#333" }}
            >
              Please send an email to <strong>support@investofarms.com</strong>{" "}
              with any questions you may have regarding this policy.
            </Typography>
          </Box>
        </Box>
      </Container>

      <Box sx={{ mt: "auto" }}>
        <InvestorBanner />
        <Footer ref={footerRef} />
      </Box>
    </Box>
  );
};

export default Refund;
